import React, { useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from '@app/core/i18n';

import { CharacterRequirement } from '@app/queryTyping';
import { getFieldError, getFieldErrorKeys } from '@app/common/configurable-wizards/utils';
import { PoliciesComponentProps, PolicyItem, PolicyUnion } from '../types';
import { composePolicies } from '../utils/composeUsernamePasswordPolices';
import { PoliciesList } from '../common/PoliciesList';
import { PasswordValidationRules, PolicySettings } from './types';

export type PasswordPolicyItem = PolicyItem<PasswordValidationRules>;
export type PasswordPolicyUnion = PolicyUnion<PasswordValidationRules>;
export interface PasswordPoliciesProps extends PoliciesComponentProps<PolicySettings> {}

export const PasswordPolicies: React.FC<PasswordPoliciesProps> = ({
  relatedInputFocused,
  policySettings,
  fieldName,
}) => {
  const { t } = useTranslation();
  const { formState: { errors } } = useFormContext();
  const fieldError = getFieldError(fieldName, errors);

  const policies = useMemo<Array<PasswordPolicyUnion>>(() => {
    if (!policySettings) {
      return [];
    }

    const genericRequirements:Array<PasswordPolicyItem> = [];
    const crossDataRequirements:Array<PasswordPolicyItem> = [];
    const charSpecificRequirements:Array<PasswordPolicyItem> = [];
    const charSpecificProhibition:Array<PasswordPolicyItem> = [];

    // GENERIC REQUIREMENTS
    if (policySettings.anyCharacterMustBeAlphabetic) {
      genericRequirements.push({
        validationRule: PasswordValidationRules.anyAlphabetic,
        title: t(
          'password.polices.any-character-must-be-alphabetic|At least one alphabetic character requirement message',
          'At least one alphabetic letter',
        ),
      });
    }

    if (policySettings.minimumCharacterLength > 0) {
      genericRequirements.push({
        validationRule: PasswordValidationRules.minLength,
        title: t(
          'password.polices.min-character-length|Minimum password length requirement message',
          'Password must be at least {{minLength}} characters long',
          { minLength: policySettings.minimumCharacterLength },
        ),
      });
    }

    if (policySettings.maximumCharacterLength > 0) {
      genericRequirements.push({
        validationRule: PasswordValidationRules.maxLength,
        title: t(
          'password.polices.max-character-length|Up to maximum password length requirement message',
          'Password can be up to {{maxLength}} characters long',
          { maxLength: policySettings.maximumCharacterLength },
        ),
      });
    }

    // CROSS-DATA REQUIREMENTS
    if (policySettings.mustNotContainBirthdate) {
      crossDataRequirements.push({
        title: t(
          'password.polices.cross-data-requirements.must-not-contain-birthdate|Must not contain birthdate requirement',
          'Your birth date',
        ),
      });
    }

    if (policySettings.mustNotContainMemberNumber) {
      crossDataRequirements.push({
        title: t(
          'password.polices.cross-data-requirements.must-not-contain-mn|Must not contain MN requirement',
          'Your member number',
        ),
      });
    }

    if (policySettings.mustNotContainSsn) {
      crossDataRequirements.push({
        title: t(
          'password.polices.cross-data-requirements.must-not-contain-ssn|Must not contain SSN requirement',
          'Your social security number',
        ),
      });
    }

    if (policySettings.mustNotContainUsername) {
      crossDataRequirements.push({
        title: t(
          'password.polices.cross-data-requirements.must-not-contain-username|Must not contain username requirement',
          'Username',
        ),
      });
    }

    // CHAR-SPECIFIC REQUIREMENTS/PROHIBITIONS
    if (policySettings.firstCharacterMustBeAlphabetic) {
      charSpecificRequirements.push({
        validationRule: PasswordValidationRules.firstCharAlphabetic,
        title: t(
          'password.polices.character-requirements.first-alphabetic|First letter must be alphabetic requirement',
          'First letter must be alphabetic',
        ),
      });
    }

    if (policySettings.lowerCaseRequirement === CharacterRequirement.ONEREQUIRED) {
      charSpecificRequirements.push({
        validationRule: PasswordValidationRules.anyLowerCase,
        title: t(
          'password.polices.character-requirements.lowercase|At least one lowercase letter requirement',
          'At least one lowercase letter (a through z)',
        ),
      });
    } else if (policySettings.lowerCaseRequirement === CharacterRequirement.NOTALLOWED) {
      charSpecificProhibition.push({
        validationRule: PasswordValidationRules.noLowerCase,
        title: t(
          'password.polices.character-prohibitions.lowercase|Lowercase letters prohibitance',
          'Lowercase letters (a through z)',
        ),
      });
    }

    if (policySettings.upperCaseRequirement === CharacterRequirement.ONEREQUIRED) {
      charSpecificRequirements.push({
        validationRule: PasswordValidationRules.anyUpperCase,
        title: t(
          'password.polices.character-requirements.one-uppercase|At least one uppercase letter requirement',
          'At least one uppercase letter (A through Z)',
        ),
      });
    } else if (policySettings.upperCaseRequirement === CharacterRequirement.NOTALLOWED) {
      charSpecificProhibition.push({
        validationRule: PasswordValidationRules.noUpperCase,
        title: t(
          'password.polices.character-prohibitions.uppercase|Uppercase letters prohibitance',
          'Uppercase letters (A through Z)',
        ),
      });
    }

    if (policySettings.numberRequirement === CharacterRequirement.ONEREQUIRED) {
      charSpecificRequirements.push({
        validationRule: PasswordValidationRules.anyNumber,
        title: t(
          'password.polices.character-requirements.one-number|At least one number requirement',
          'At least one number',
        ),
      });
    } else if (policySettings.numberRequirement === CharacterRequirement.NOTALLOWED) {
      charSpecificProhibition.push({
        validationRule: PasswordValidationRules.noNumber,
        title: t(
          'password.polices.character-prohibitions.number|Numbers prohibitance',
          'Numbers',
        ),
      });
    }

    if (policySettings.specialCharacterRequirement === CharacterRequirement.ONEREQUIRED) {
      charSpecificRequirements.push({
        validationRule: PasswordValidationRules.anySpecialChar,
        title: t(
          'password.polices.character-requirements.one-special-char|At least one special character requirement',
          'At least one special character',
        ),
      });
    } else if (policySettings.specialCharacterRequirement === CharacterRequirement.NOTALLOWED) {
      charSpecificProhibition.push({
        validationRule: PasswordValidationRules.noSpecialChar,
        title: t(
          'password.polices.character-prohibitions.special-char|Special characters prohibitance',
          'Special characters',
        ),
      });
    }

    return composePolicies<PasswordValidationRules>({
      genericRequirements,
      crossDataRequirements: {
        title: t('password.polices.cross-data-requirements|List of password cross-data requirements',
          'Your password must not contain the following:'),
        policies: crossDataRequirements,
      },
      charRequirements: {
        title: t('password.polices.character-requirements|List of password character requirements',
          'Your password must contain the following:'),
        policies: charSpecificRequirements,
      },
      charProhibitions: {
        title: t('password.polices.character-prohibitions|List of password character prohibitions',
          'Your password must not contain the following:'),
        policies: charSpecificProhibition,
      },
    });
  }, [policySettings]);

  return (
    <PoliciesList
      currentErrorKeys={getFieldErrorKeys(fieldError)}
      relatedInputFocused={relatedInputFocused}
      policies={policies}
    />
  );
};
